import React from 'react'
import { Modal, Table, Badge } from 'react-bootstrap'

const API = () => <Badge bg="info" className="me-1" style={{ fontSize: '0.7rem' }}>API</Badge>
const CALC = () => <Badge bg="secondary" className="me-1" style={{ fontSize: '0.7rem' }}>CALC</Badge>

function MonthlyProfitabilityGuideModal({ show, onHide }) {
    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Monthly Profitability &mdash; Column Reference</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-3">
                    <API /> Value from Amazon API (may be aggregated)
                    &nbsp;&nbsp;
                    <CALC /> Calculated by the system
                </p>

                <h6 className="mb-2">Data Source</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                    Uses pre-collected monthly DataKiosk economics records when available.
                    Falls back to aggregating daily records for the month.
                    Ad spend comes from the monthly ad summary or daily ad performance records.
                </p>

                <h6 className="mb-2">Column Definitions</h6>
                <Table size="sm" bordered hover>
                    <thead>
                        <tr>
                            <th>Column</th>
                            <th style={{ width: '90px', textAlign: 'center' }}>Source</th>
                            <th>Calculation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="table-active"><td colSpan="3"><strong>Identity</strong></td></tr>
                        <tr>
                            <td>ASIN</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From DataKiosk economics records for the selected month</td>
                        </tr>
                        <tr>
                            <td>SKU</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Amazon SKU from economics data</td>
                        </tr>
                        <tr>
                            <td>Product Name</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>From SKU Mapping <code>adv_alias</code>; fallback to FBA inventory product name, then SKU Mapping name</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Pricing &amp; Fees (per unit)</strong></td></tr>
                        <tr>
                            <td>Sale Price</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>revenue / units_sold</code>; fallback to FBA inventory <code>your_price</code></td>
                        </tr>
                        <tr>
                            <td>FBA Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit gross <code>FbaFulfilment</code> fee from DataKiosk (includes base fulfillment + fuel surcharge)</td>
                        </tr>
                        <tr>
                            <td>Inbound Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>FbaInboundConvenience</code> (placement) + <code>FbaInboundTransportation</code> fee</td>
                        </tr>
                        <tr>
                            <td>Referral Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>Referral</code> fee from DataKiosk economics records</td>
                        </tr>
                        <tr>
                            <td>Storage Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>FbaStorage</code> + <code>LongTermStorage</code> + <code>STARStorage</code> fee</td>
                        </tr>
                        <tr>
                            <td>AWD Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>AmazonUpstreamStorage</code> + <code>AmazonUpstreamOutbound</code> fee (AWD warehouse)</td>
                        </tr>
                        <tr>
                            <td>Deal Fee</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>Per-unit <code>DealParticipation</code> + <code>DealPerformance</code> fee (Lightning Deals, coupons)</td>
                        </tr>
                        <tr>
                            <td>COGS</td>
                            <td style={{ textAlign: 'center' }}><API /><CALC /></td>
                            <td>From DataKiosk <code>costOfGoodsSold</code>; fallback to latest receiving cost from SKU Mapping stock item</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Profitability</strong></td></tr>
                        <tr>
                            <td>Profit</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>sale_price &minus; (cogs + fba_fee + inbound_fee + referral_fee + storage_fee + awd_fee + deal_fee + ad_spend_per_unit)</code></td>
                        </tr>
                        <tr>
                            <td>Margin %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(profit / sale_price) &times; 100</code><br /><small className="text-muted">Standard profit margin &mdash; what percentage of the selling price is profit</small></td>
                        </tr>
                        <tr>
                            <td>BE ACoS %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>((sale_price &minus; cost_before_ad) / sale_price) &times; 100</code><br /><small className="text-muted">cost_before_ad = cogs + all fees (excl. ad spend)</small></td>
                        </tr>
                        <tr>
                            <td>Max CPA</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>sale_price &minus; cost_before_ad</code> (contribution margin in dollars &mdash; max you can spend per acquisition)</td>
                        </tr>
                        <tr>
                            <td>Return on Cost %</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td><code>(profit / cogs) &times; 100</code><br /><small className="text-muted">Profit relative to cost of goods &mdash; higher = more return per dollar of product cost</small></td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Sales</strong></td></tr>
                        <tr>
                            <td>Units Sold</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Total <code>unitsOrdered</code> from DataKiosk economics for the month</td>
                        </tr>
                        <tr>
                            <td>Units Returned</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Total <code>unitsRefunded</code> from DataKiosk economics for the month</td>
                        </tr>
                        <tr>
                            <td>Revenue</td>
                            <td style={{ textAlign: 'center' }}><API /></td>
                            <td>Total <code>orderedProductSales</code> from DataKiosk economics for the month</td>
                        </tr>

                        <tr className="table-active"><td colSpan="3"><strong>Inventory</strong></td></tr>
                        <tr>
                            <td>Inventory Level</td>
                            <td style={{ textAlign: 'center' }}><CALC /></td>
                            <td>
                                <code>month_supply = afn_total_quantity / ((units_sold / days_in_month) &times; 30)</code>
                                <br /><small className="text-muted">0 &rarr; Out of stock; &lt; <code>inventory_low_stock_months</code> (default 2) &rarr; Low stock; else Enough</small>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Modal.Body>
        </Modal>
    )
}

export default MonthlyProfitabilityGuideModal
